import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import StorageIcon from '@mui/icons-material/Storage';
import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Select,
    Stack,
    Toolbar,
    Tooltip,
    Typography
} from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import logo from '../assets/logo.svg';
import { useAuth } from '../auth/AuthContext';
import { CATEGORIES } from '../models/Models';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entityKey } = useParams();
  const { name, email, can, logout } = useAuth();

  const canWrite = can('create');

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const isDatabase = location.pathname.startsWith('/database');
  const isComparator = location.pathname.startsWith('/comparator');
  const isFaq = location.pathname.startsWith('/faq');

  const navBtnSx = (active: boolean) => ({
    bgcolor: active ? 'rgba(255,255,255,0.22)' : 'transparent',
    color: 'white',
    borderRadius: '10px',
    '&:hover': { bgcolor: 'rgba(255,255,255,0.18)' },
  });

  const current = CATEGORIES.find((c) => c.value === entityKey)?.value ?? 'suppliers';

  const initials = name
    ? name
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?';

  return (
    <AppBar position="sticky" elevation={0}>
      <Toolbar sx={{ gap: 2, minHeight: 60 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '8px',
              bgcolor: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={logo} width={18} height={18} alt="TechnoDB logo" />
          </Box>
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.05rem' }}
          >
            TechnoDB
          </Typography>
        </Stack>

        <Box sx={{ height: 24, width: '1px', bgcolor: 'rgba(255,255,255,0.3)', mx: 1 }} />

        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Database">
            <IconButton
              onClick={() => navigate('/database/entities/suppliers')}
              sx={navBtnSx(isDatabase)}
            >
              <StorageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Comparator">
            <IconButton
              onClick={() => navigate('/comparator')}
              sx={navBtnSx(isComparator)}
            >
              <CompareArrowsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="FAQ">
            <IconButton
              onClick={() => navigate('/faq')}
              sx={navBtnSx(isFaq)}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {isDatabase && (
          <>
            <Box sx={{ height: 24, width: '1px', bgcolor: 'rgba(255,255,255,0.3)', mx: 1 }} />
            <Select
              size="small"
              value={current}
              onChange={(e) => navigate(`/database/entities/${e.target.value}`)}
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                minWidth: 220,
                color: 'white',
                fontWeight: 600,
                backgroundColor: 'rgba(255,255,255,0.12)',
                borderRadius: '999px',
                '& .MuiSvgIcon-root': { color: 'white' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                '& .MuiSelect-select': { py: 0.75, pl: 2 },
              }}
            >
              {CATEGORIES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            ml: 0.5,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontSize: '0.8rem',
              fontWeight: 700,
            }}
          >
            {initials}
          </Avatar>
        </IconButton>

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                minWidth: 220,
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,42,92,0.15)',
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#002A5C' }}>
              {name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#5C6B7A' }}>
              {email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem disabled>
            <ListItemIcon>
              <PersonOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={canWrite ? 'Administrator' : 'Reader'}
              secondary={canWrite ? 'Full access' : 'Read-only access'}
            />
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              logout();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#E30613' }} />
            </ListItemIcon>
            <ListItemText primary="Sign out" sx={{ '& .MuiTypography-root': { color: '#E30613', fontWeight: 600 } }} />
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
